import React, { useState, useMemo, useEffect } from 'react';
import { X, Plus, Package2, Trash2, Check, AlertCircle, Split } from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { TransitPackagingDto } from '../../../../../types/shipmentLine';
import { ExtendedPickingLine } from '../hooks/usePickingData';
import { NumericInput } from '../../../../common/inputs';

/**
 * Lastbærer-fordeling ved avslutning av plukk (Teamwork 36334239).
 *
 * Operatøren oppretter X lastbærere øverst og fordeler plukklinjene på dem i en
 * todelt skjerm. Interaksjon er TAPP-basert (ikke drag & drop — vedtatt fordi
 * touch-drag treffer dårlig på nettbrett):
 *   tapp linje → tapp bærer  = knytt (hele gjenstående antall)
 *   tapp linje inne i bærer  = frigi
 *   «Del»-knapp på linjen    = fordel antallet mellom flere bærere
 *                              (samme mønster som QuantityDistributionModal i plukk)
 *
 * Modalen er ren presentasjon: den returnerer fordelingen, og kalleren
 * (usePickingCompletion) persisterer den som ekte paller (OrderSSCC) + kollilinjer
 * — nøyaktig samme datavei som når bærere registreres på forhånd i pallpanelet.
 *
 * Under plukk (TW 36396316) gjenbrukes modalen fra pallpanelet: existingCarriers er de
 * eksisterende pallene, seedet med det som allerede ligger på dem (TW 36397752). Dermed kan
 * operatøren se innholdet per lastbærer, frigi linjer tilbake til «ikke fordelt» og flytte
 * dem mellom bærere. Kalleren regner ut differansene mot dagens plassering ved lagring.
 * placedQtyByLine er alternativet for kallere som vil holde plasserte varer utenfor dialogen.
 */

/** Én lastbærer med de plukklinjene (og antallene) operatøren har lagt på den. */
export interface CarrierDistributionResult {
  transitPackagingID: number;
  transitPackagingDescr: string;
  /** Visningsnavn i dialogen — SSCC tildeles ved lagring */
  name: string;
  /** Satt når bæreren er en eksisterende pall (fordeling under plukk) */
  sscc?: string;
  lines: Array<{ pListLineNo: number; orderNo: number; qty: number }>;
}

/** Eksisterende lastbærer som kan ta imot varer (fordeling under plukk). */
export interface ExistingDistributionCarrier {
  sscc: string;
  name: string;
  transitPackagingID: number;
  transitPackagingDescr: string;
  /** Det som allerede ligger på pallen, per plukklinje (TW 36397752). Vises og kan flyttes/frigis. */
  lines?: Array<{ pListLineNo: number; qty: number }>;
}

interface CarrierAssignment {
  pListLineNo: number;
  qty: number;
}

interface DistributionCarrier {
  /** Lokal nøkkel — bæreren finnes bare i denne dialogen til den lagres som kollilinje */
  key: string;
  name: string;
  transitPackagingID: number;
  transitPackagingDescr: string;
  sscc?: string;
  lines: CarrierAssignment[];
}

interface CarrierDistributionModalProps {
  isOpen: boolean;
  /** Plukklinjer som skal fordeles (kun linjer med plukket antall > 0) */
  pickingLines: ExtendedPickingLine[];
  /** Lastbærer-typene fra TransitPackaging */
  carriers: TransitPackagingDto[];
  /** Kalles med fordelingen — kalleren oppretter paller og kollilinjer */
  onConfirm: (result: CarrierDistributionResult[]) => void;
  onClose: () => void;
  saving?: boolean;
  /** Antall per pListLineNo som allerede ligger på en pall (fordeling under plukk) */
  placedQtyByLine?: Record<number, number>;
  /** Eksisterende paller som mål (fordeling under plukk). Satt = dialogen nullstilles ved åpning. */
  existingCarriers?: ExistingDistributionCarrier[];
  /** Overstyrer overskrift og bekreft-tekst (fordeling under plukk) */
  title?: string;
  confirmLabel?: string;
}

export const CarrierDistributionModal: React.FC<CarrierDistributionModalProps> = ({
  isOpen,
  pickingLines,
  carriers,
  onConfirm,
  onClose,
  saving = false,
  placedQtyByLine,
  existingCarriers,
  title,
  confirmLabel
}) => {
  const { t } = useTranslation();

  const [distCarriers, setDistCarriers] = useState<DistributionCarrier[]>([]);
  const [selectedLineNo, setSelectedLineNo] = useState<number | null>(null);
  const [newCarrierTypeId, setNewCarrierTypeId] = useState<number | null>(null);
  const [newCarrierCount, setNewCarrierCount] = useState('1');
  const [nextCarrierNo, setNextCarrierNo] = useState(1);
  // Delmengde-fordeling: linjen som skal splittes + antall per bærer
  const [splitLineNo, setSplitLineNo] = useState<number | null>(null);
  const [splitQuantities, setSplitQuantities] = useState<{ [carrierKey: string]: number }>({});

  // Fordeling under plukk: start hver åpning med de eksisterende pallene som (tomme) mål
  useEffect(() => {
    if (!isOpen || !existingCarriers) return;
    setDistCarriers(existingCarriers.map(c => ({
      key: `S-${c.sscc}`,
      name: c.name,
      transitPackagingID: c.transitPackagingID,
      transitPackagingDescr: c.transitPackagingDescr,
      sscc: c.sscc,
      lines: (c.lines ?? []).filter(l => l.qty > 0).map(l => ({ pListLineNo: l.pListLineNo, qty: l.qty }))
    })));
    setNextCarrierNo(existingCarriers.length + 1);
    setSelectedLineNo(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Default lastbærer-type = TransitPackaging.DefaultYesNo (som i pallpanelet)
  useEffect(() => {
    if (!isOpen || carriers.length === 0) return;
    setNewCarrierTypeId(prev => prev ?? (carriers.find(c => c.defaultYesNo)?.transitPackagingID ?? carriers[0].transitPackagingID));
  }, [isOpen, carriers]);

  // Kun linjer som faktisk er plukket kan legges på en lastbærer
  const pickedLines = useMemo(
    () => pickingLines.filter(l => (l.qtyPicked ?? 0) > 0),
    [pickingLines]
  );

  const assignedQty = (pListLineNo: number) =>
    distCarriers.reduce((sum, c) => sum + c.lines
      .filter(l => l.pListLineNo === pListLineNo)
      .reduce((s, l) => s + l.qty, 0), 0);

  /** Det som kan fordeles: plukket minus det som allerede ligger på en pall */
  const distributableQty = (line: ExtendedPickingLine) =>
    Math.max(0, (line.qtyPicked ?? 0) - (placedQtyByLine?.[line.pListLineNo] ?? 0));

  const remainingQty = (line: ExtendedPickingLine) =>
    distributableQty(line) - assignedQty(line.pListLineNo);

  const unassignedLines = pickedLines.filter(l => remainingQty(l) > 0);
  const selectedLine = pickedLines.find(l => l.pListLineNo === selectedLineNo) ?? null;
  const splitLine = pickedLines.find(l => l.pListLineNo === splitLineNo) ?? null;

  const handleAddCarriers = () => {
    if (newCarrierTypeId == null) return;
    const type = carriers.find(c => c.transitPackagingID === newCarrierTypeId);
    if (!type) return;

    let count = parseInt(newCarrierCount, 10);
    if (!Number.isFinite(count) || count < 1) count = 1;
    if (count > 20) count = 20; // rimelighetsgrense — hindrer feiltasting som lager 500 bærere

    const added: DistributionCarrier[] = [];
    for (let i = 0; i < count; i++) {
      const no = nextCarrierNo + i;
      added.push({
        key: `C${no}`,
        name: `${type.transitPackagingDescr} ${no}`,
        transitPackagingID: type.transitPackagingID,
        transitPackagingDescr: type.transitPackagingDescr,
        lines: []
      });
    }
    setDistCarriers(prev => [...prev, ...added]);
    setNextCarrierNo(no => no + count);
  };

  const handleRemoveCarrier = (key: string) => {
    // Linjene på bæreren blir automatisk ledige igjen (de er kun referanser)
    setDistCarriers(prev => prev.filter(c => c.key !== key));
  };

  /** Tapp bærer med en valgt linje → legg hele gjenstående antall der */
  const handleCarrierTap = (carrierKey: string) => {
    if (!selectedLine) return;
    const rest = remainingQty(selectedLine);
    if (rest <= 0) return;

    setDistCarriers(prev => prev.map(c => {
      if (c.key !== carrierKey) return c;
      const existing = c.lines.find(l => l.pListLineNo === selectedLine.pListLineNo);
      return existing
        ? { ...c, lines: c.lines.map(l => l.pListLineNo === selectedLine.pListLineNo ? { ...l, qty: l.qty + rest } : l) }
        : { ...c, lines: [...c.lines, { pListLineNo: selectedLine.pListLineNo, qty: rest }] };
    }));
    setSelectedLineNo(null);
  };

  /** Tapp linje inne i en bærer → frigi den (antallet blir ledig igjen) */
  const handleReleaseLine = (carrierKey: string, pListLineNo: number) => {
    setDistCarriers(prev => prev.map(c =>
      c.key === carrierKey ? { ...c, lines: c.lines.filter(l => l.pListLineNo !== pListLineNo) } : c
    ));
  };

  // ── Delmengde-fordeling (samme mønster som QuantityDistributionModal i plukk) ──
  const openSplit = (line: ExtendedPickingLine) => {
    const current: { [key: string]: number } = {};
    distCarriers.forEach(c => {
      const a = c.lines.find(l => l.pListLineNo === line.pListLineNo);
      if (a) current[c.key] = a.qty;
    });
    setSplitQuantities(current);
    setSplitLineNo(line.pListLineNo);
  };

  const splitDistributed = Object.values(splitQuantities).reduce((s, q) => s + (q || 0), 0);
  const splitMax = splitLine ? distributableQty(splitLine) : 0;

  const confirmSplit = () => {
    if (!splitLine) return;
    setDistCarriers(prev => prev.map(c => {
      const qty = splitQuantities[c.key] || 0;
      const others = c.lines.filter(l => l.pListLineNo !== splitLine.pListLineNo);
      return { ...c, lines: qty > 0 ? [...others, { pListLineNo: splitLine.pListLineNo, qty }] : others };
    }));
    setSplitLineNo(null);
    setSplitQuantities({});
  };

  /** Fordelingen som kalleren skal persistere: nye bærere kun når de har linjer; eksisterende
   *  paller alltid (også tømte), slik at kalleren kan fjerne det som ble frigitt (TW 36397752). */
  const buildResult = (): CarrierDistributionResult[] =>
    distCarriers
      .filter(c => c.lines.length > 0 || !!c.sscc)
      .map(c => ({
        transitPackagingID: c.transitPackagingID,
        transitPackagingDescr: c.transitPackagingDescr,
        name: c.name,
        sscc: c.sscc,
        lines: c.lines.map(a => ({
          pListLineNo: a.pListLineNo,
          orderNo: pickedLines.find(l => l.pListLineNo === a.pListLineNo)?.orderNo ?? 0,
          qty: a.qty
        }))
      }));

  if (!isOpen) return null;

  const usedCarriers = distCarriers.filter(c => c.lines.length > 0);
  // Med eksisterende paller er «alt frigitt» også en gyldig lagring (TW 36397752)
  const canConfirm = !saving && (usedCarriers.length > 0 || distCarriers.some(c => !!c.sscc));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white w-full h-full sm:h-auto sm:max-h-[92vh] sm:rounded-lg sm:max-w-5xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Package2 size={20} className="text-teal-600" />
            {title ?? t('warehouse.modules.picking.carrierDistribution.title', {}, 'Registrer lastbærere')}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label={t('common.buttons.close', {}, 'Lukk')}
          >
            <X size={24} />
          </button>
        </div>

        {/* Opprett lastbærere */}
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex flex-wrap items-end gap-3 flex-shrink-0">
          <div>
            <label htmlFor="dist-carrier-type" className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
              {t('warehouse.modules.picking.pallets.carrier', {}, 'Lastbærer')}
            </label>
            <select
              id="dist-carrier-type"
              value={newCarrierTypeId ?? ''}
              onChange={(e) => setNewCarrierTypeId(e.target.value ? Number(e.target.value) : null)}
              className="p-2 min-h-[44px] border border-gray-300 rounded bg-white"
            >
              {carriers.map(c => (
                <option key={c.transitPackagingID} value={c.transitPackagingID}>{c.transitPackagingDescr}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="dist-carrier-count" className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
              {t('common.fields.quantity', {}, 'Antall')}
            </label>
            <NumericInput
              id="dist-carrier-count"
              mode="integer"
              value={newCarrierCount}
              onChange={(e) => setNewCarrierCount(e.target.value)}
              className="w-20 p-2 min-h-[44px] border border-gray-300 rounded text-center"
            />
          </div>
          <button
            onClick={handleAddCarriers}
            disabled={newCarrierTypeId == null}
            className="flex items-center gap-1.5 px-4 py-2 min-h-[44px] bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 disabled:opacity-50"
          >
            <Plus size={16} />
            {t('warehouse.modules.picking.carrierDistribution.create', {}, 'Opprett')}
          </button>
          <p className="text-xs text-gray-500 ml-auto max-w-xs">
            {t('warehouse.modules.picking.carrierDistribution.hint', {},
              'Tapp en plukklinje, og deretter lastbæreren den skal på.')}
          </p>
        </div>

        {/* Todelt: plukklinjer | lastbærere */}
        <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-2 overflow-hidden">
          {/* Venstre: ufordelte plukklinjer */}
          <div className="overflow-y-auto p-3 border-b md:border-b-0 md:border-r border-gray-200">
            <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">
              {t('warehouse.modules.picking.carrierDistribution.unassigned', { '0': String(unassignedLines.length) },
                `Plukklinjer — ikke fordelt (${unassignedLines.length})`)}
            </h4>
            {unassignedLines.length === 0 ? (
              <div className="text-center text-sm text-gray-400 py-8">
                <Check size={28} className="mx-auto mb-2 text-green-500" />
                {t('warehouse.modules.picking.carrierDistribution.allAssigned', {}, 'Alle linjer er fordelt')}
              </div>
            ) : unassignedLines.map(line => {
              const rest = remainingQty(line);
              const isSelected = selectedLineNo === line.pListLineNo;
              return (
                <div key={line.pListLineNo} className="flex items-stretch gap-2 mb-2">
                  <button
                    onClick={() => setSelectedLineNo(isSelected ? null : line.pListLineNo)}
                    className={`flex-1 flex items-center gap-2 text-left rounded-lg border-2 px-3 py-2 min-h-[52px] transition-colors ${
                      isSelected ? 'border-blue-600 bg-blue-50' : 'border-gray-200 bg-white hover:border-blue-300'
                    }`}
                  >
                    <span className="font-mono font-semibold text-sm">{line.itemNo}</span>
                    <span className="flex-1 text-sm text-gray-700 truncate">{line.itemDescription}</span>
                    <span className={`font-mono text-sm ${isSelected ? 'text-blue-700 font-semibold' : 'text-gray-500'}`}>
                      {rest}
                    </span>
                  </button>
                  {rest > 1 && distCarriers.length > 1 && (
                    <button
                      onClick={() => openSplit(line)}
                      className="px-3 min-h-[52px] rounded-lg border-2 border-gray-200 bg-white text-gray-500 hover:border-teal-400 hover:text-teal-600"
                      title={t('warehouse.modules.picking.carrierDistribution.splitTooltip', {}, 'Fordel antallet mellom flere lastbærere')}
                    >
                      <Split size={16} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Høyre: lastbærerne */}
          <div className="overflow-y-auto p-3">
            <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">
              {t('warehouse.modules.picking.carrierDistribution.carriers', { '0': String(distCarriers.length) },
                `Lastbærere (${distCarriers.length})`)}
            </h4>
            {distCarriers.length === 0 ? (
              <div className="text-center text-sm text-gray-400 py-8">
                <Package2 size={28} className="mx-auto mb-2 text-gray-300" />
                {t('warehouse.modules.picking.carrierDistribution.noCarriers', {}, 'Opprett en lastbærer for å starte fordelingen')}
              </div>
            ) : distCarriers.map(carrier => {
              const totalQty = carrier.lines.reduce((s, l) => s + l.qty, 0);
              return (
                <div
                  key={carrier.key}
                  onClick={() => handleCarrierTap(carrier.key)}
                  className={`mb-2 rounded-lg border-2 overflow-hidden transition-all ${
                    selectedLine ? 'border-teal-600 ring-2 ring-teal-200 cursor-pointer' : 'border-teal-100'
                  }`}
                >
                  <div className="flex items-center gap-2 px-3 py-2 bg-teal-50 border-b border-teal-100">
                    <span className="font-bold text-sm text-teal-900">{carrier.name}</span>
                    <span className="text-xs text-teal-700 bg-teal-100 rounded-full px-2 py-0.5">
                      {carrier.transitPackagingDescr}
                    </span>
                    <span className="ml-auto font-mono text-xs text-teal-700">
                      {t('warehouse.modules.picking.carrierDistribution.carrierSummary',
                        { '0': String(carrier.lines.length), '1': String(totalQty) },
                        `${carrier.lines.length} linjer · ${totalQty} stk`)}
                    </span>
                    {!carrier.sscc && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleRemoveCarrier(carrier.key); }}
                        className="text-red-500 hover:text-red-700 min-h-[36px] min-w-[36px] flex items-center justify-center"
                        title={t('warehouse.modules.picking.carrierDistribution.removeCarrier', {}, 'Fjern lastbærer')}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  <div className="px-2 py-1.5 min-h-[38px]">
                    {carrier.lines.length === 0 ? (
                      <div className="text-xs text-gray-400 px-2 py-1">
                        {t('warehouse.modules.picking.carrierDistribution.emptyCarrier', {}, 'Tom — tapp for å legge valgt linje her')}
                      </div>
                    ) : carrier.lines.map(a => {
                      const line = pickedLines.find(l => l.pListLineNo === a.pListLineNo);
                      return (
                        <button
                          key={a.pListLineNo}
                          onClick={(e) => { e.stopPropagation(); handleReleaseLine(carrier.key, a.pListLineNo); }}
                          className="w-full flex items-center gap-2 text-left text-xs rounded border border-gray-200 bg-gray-50 px-2.5 py-1.5 my-1 hover:border-red-300 hover:bg-red-50 min-h-[36px]"
                          title={t('warehouse.modules.picking.carrierDistribution.releaseTooltip', {}, 'Tapp for å frigi linjen')}
                        >
                          <span className="font-mono font-semibold">{line?.itemNo}</span>
                          <span className="flex-1 truncate text-gray-600">{line?.itemDescription}</span>
                          <span className="font-mono text-gray-700">{a.qty}</span>
                          <X size={12} className="text-red-500 flex-shrink-0" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 flex flex-col sm:flex-row gap-2 sm:items-center flex-shrink-0">
          {unassignedLines.length > 0 && usedCarriers.length > 0 && (
            <span className="text-xs text-amber-700 flex items-center gap-1.5">
              <AlertCircle size={14} />
              {t('warehouse.modules.picking.carrierDistribution.someUnassigned', { '0': String(unassignedLines.length) },
                `${unassignedLines.length} linje(r) er ikke fordelt — de blir ikke knyttet til noen lastbærer`)}
            </span>
          )}
          <div className="sm:ml-auto flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 min-h-[44px] border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              {t('common.buttons.cancel', {}, 'Avbryt')}
            </button>
            <button
              onClick={() => onConfirm(buildResult())}
              disabled={!canConfirm}
              className="px-5 py-2 min-h-[44px] bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 disabled:opacity-50"
            >
              {confirmLabel ?? t('warehouse.modules.picking.carrierDistribution.confirm', { '0': String(usedCarriers.length) },
                `Registrer ${usedCarriers.length} lastbærer(e)`)}
            </button>
          </div>
        </div>
      </div>

      {/* Delmengde-fordeling: samme mønster som QuantityDistributionModal i plukk */}
      {splitLine && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4" onClick={() => setSplitLineNo(null)}>
          <div className="bg-white rounded-lg max-w-md w-full p-4" onClick={(e) => e.stopPropagation()}>
            <h4 className="font-semibold mb-1">
              {t('warehouse.modules.picking.carrierDistribution.splitTitle', {}, 'Fordel antall mellom lastbærere')}
            </h4>
            <p className="text-sm text-gray-600 mb-3">
              {splitLine.itemNo} — {splitLine.itemDescription}<br />
              {t('warehouse.modules.picking.carrierDistribution.splitSummary',
                { '0': String(splitMax), '1': String(splitDistributed), '2': String(splitMax - splitDistributed) },
                `Plukket: ${splitMax} | Fordelt: ${splitDistributed} | Rest: ${splitMax - splitDistributed}`)}
            </p>
            <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
              {distCarriers.map(c => (
                <div key={c.key} className="flex items-center gap-3 p-2 border rounded-lg">
                  <span className="flex-1 text-sm font-medium">{c.name}</span>
                  <NumericInput
                    mode="integer"
                    quantity
                    min="0"
                    max={String(splitMax)}
                    value={splitQuantities[c.key] ? String(splitQuantities[c.key]) : ''}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10) || 0;
                      setSplitQuantities(prev => ({ ...prev, [c.key]: v }));
                    }}
                    className="w-24 p-2 min-h-[44px] border border-gray-300 rounded text-center"
                  />
                </div>
              ))}
            </div>
            {splitDistributed > splitMax && (
              <p className="text-sm text-red-600 mb-2 flex items-center gap-1.5">
                <AlertCircle size={14} />
                {t('warehouse.modules.picking.carrierDistribution.splitTooMuch', {}, 'Du har fordelt mer enn det som er plukket.')}
              </p>
            )}
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setSplitLineNo(null)}
                className="px-4 py-2 min-h-[44px] border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                {t('common.buttons.cancel', {}, 'Avbryt')}
              </button>
              <button
                onClick={confirmSplit}
                disabled={splitDistributed > splitMax}
                className="px-4 py-2 min-h-[44px] bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 disabled:opacity-50"
              >
                {t('common.buttons.ok', {}, 'OK')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
